import type { AssetClass, UserInstrumentClassificationDto } from "@eu.mdotm.api/sphere";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { qualifier } from "$root/utils";
import {
	type Option,
	type OrderBy,
	type TableColumn,
	BaseTable,
	Controller,
	DebouncedSearchInput,
	Dialog,
	DialogFooter,
	sortRows,
	Text,
	useSelectableTableColumn,
} from "@mdotm/shadcn/legacy/components";
import { Button, Radio, RadioGroup, Select, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import { type MultiSelectCtx, useMultiSelect, useSearchable } from "@mdotm/shadcn/legacy/headless";
import type { MaybePromise } from "@mdotm/utils/promise";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { builtInSortFnFor, find, typedObjectValues } from "@mdotm/shadcn/legacy/utils";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ClassificationOptionsTable } from "./spawn-tag-classifications";
import { type IdentifiedClassification, cleanClassificationId, userClassificationId } from "./classification-id";

const AssetAllocationType = {
	tag: "TAG",
	riskModel: "RISK_MODEL",
} as const;

type AssetAllocationType = (typeof AssetAllocationType)[keyof typeof AssetAllocationType];

type RiskModelAssetAllocation = {
	type: "RISK_MODEL";
	selectedOptions: Array<AssetClass>;
};

type TagAssetAllocation = {
	type: "TAG";
	selectedOptions: Array<string /** option */>;
	instrumentClassification: IdentifiedClassification;
};

type TagAssetAllocationDraft = {
	type: "TAG";
	instrumentClassification?: UserInstrumentClassificationDto;
};

type AssetAllocationPayload = TagAssetAllocation | RiskModelAssetAllocation;

type AssetAllocationDialogProps = MinimumDialogProps & SpawnAssetAllocationDialogProps;

export function generateAssetClassId(x: AssetClass): string {
	return `${x.identifier}-${x.macroAssetClass}-${x.microGeography}-${x.name}-${x.microAssetClass}`;
}

function AssetAllocationDialog({
	show,
	onAnimationStateChange,
	onClose,
	onSubmit,
	optionsProvider,
	selectableUserClassifications,
	selectableAssetClasses,
	disabledRowIds,
	mode = "radio",
	initialValue,
	classificationType,
}: AssetAllocationDialogProps) {
	const { t } = useTranslation();

	const selectableAssetClassOptions = useMemo(
		() =>
			selectableUserClassifications.filter(
				(classification) => classification.tagType === "ASSET_CLASS" && classification.fieldType === "TAG",
			),
		[selectableUserClassifications],
	);

	const [selectableTag, setSelectableTag] = useState<string[] | undefined>(undefined);
	const [assetAllocation, setAssetAllocation] = useState<
		TagAssetAllocationDraft | Omit<RiskModelAssetAllocation, "selectedOptions">
	>(
		classificationType === "RISK_MODEL"
			? { type: "RISK_MODEL" }
			: {
					type: "TAG",
					instrumentClassification: selectableAssetClassOptions.find(
						(classification) => initialValue?.classificationUuid === classification.classificationUuid,
					),
				},
	);

	const tagMultiSelectCtx = useMultiSelect<string>({
		selectable: selectableTag,
	});

	const riskModelMultiSelectCtx = useMultiSelect({
		selectable: selectableAssetClasses
			.map((x) => generateAssetClassId(x))
			.filter((id) => !disabledRowIds?.includes(id)),
	});

	useEffect(() => {
		if (classificationType === "RISK_MODEL" && initialValue?.selectedOptions && show) {
			riskModelMultiSelectCtx.setSelection(initialValue.selectedOptions.map((x) => generateAssetClassId(x)));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValue?.selectedOptions, show]);

	useEffect(() => {
		if (classificationType === "TAG" && initialValue?.selectedOptions && initialValue?.classificationUuid && show) {
			tagMultiSelectCtx.setSelection(
				initialValue.selectedOptions.map((tag) => userClassificationId(initialValue.classificationUuid, tag)),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValue?.selectedOptions, show]);

	const rows = useMemo(
		() =>
			selectableAssetClasses.map((x) => ({
				...x,
				id: generateAssetClassId(x),
			})) ?? [],
		[selectableAssetClasses],
	);

	return (
		<Dialog
			size="xxlarge"
			show={show}
			onAnimationStateChange={onAnimationStateChange}
			onClose={onClose}
			header="Select"
			onSubmitAsync={() => {
				if (assetAllocation.type === "RISK_MODEL") {
					const selection = riskModelMultiSelectCtx.selection;
					const selectedOptions = rows.filter((x) => selection.has(x.id)).map(({ id: _id, ...rest }) => rest);
					onSubmit({
						type: assetAllocation.type,
						selectedOptions,
					});
					return;
				}

				const classificationUuid = assetAllocation.instrumentClassification?.classificationUuid;
				if (!classificationUuid) {
					return;
				}
				const selectedOptions = tagMultiSelectCtx.selection
					.valueSeq()
					.toArray()
					.filter((s) => s.includes(classificationUuid))
					.map((selectionId) => cleanClassificationId(selectionId, classificationUuid));

				onSubmit({
					instrumentClassification: { ...assetAllocation.instrumentClassification, classificationUuid },
					type: assetAllocation.type,
					selectedOptions,
				});
			}}
			footer={
				<DialogFooter
					primaryAction={
						<SubmitButton
							palette="primary"
							disabled={
								assetAllocation.type === "TAG" && !assetAllocation.instrumentClassification?.classificationUuid
									? true
									: undefined
							}
							data-qualifier={qualifier.component.button("Dialog/Submit")}
						>
							{t("BUTTON.DONE")}
						</SubmitButton>
					}
					neutralAction={
						<Button palette="tertiary" onClick={onClose} data-qualifier={qualifier.component.button("Dialog/Cancel")}>
							{t("BUTTON.CANCEL")}
						</Button>
					}
				/>
			}
		>
			<Text type="Body/M/Medium" className="mb-1" as="p">
				Apply constraint on
			</Text>
			<RadioGroup
				onChange={(newAllocationType) => {
					if (newAllocationType === "TAG") {
						const firstCustomAllocationConstraint = selectableAssetClassOptions.at(0);

						setAssetAllocation({
							type: newAllocationType,
							instrumentClassification: firstCustomAllocationConstraint,
						});
						return;
					}

					setAssetAllocation({ type: newAllocationType });
				}}
				value={assetAllocation.type}
				className="mb-2"
			>
				<div className="flex flex-row flex-wrap gap-4">
					<Radio
						data-qualifier={`AssetAllocationDialog/ApplyConstraintOn/${AssetAllocationType.riskModel}`}
						value={AssetAllocationType.riskModel}
					>
						Risk Model
					</Radio>
					<Radio
						data-qualifier={`AssetAllocationDialog/ApplyConstraintOn/${AssetAllocationType.riskModel}`}
						value={AssetAllocationType.tag}
					>
						Asset Allocation Tags
					</Radio>
				</div>
			</RadioGroup>
			<Text type="Body/M/Book" className="mb-4" as="p">
				{assetAllocation.type === "RISK_MODEL"
					? "Choose to apply constraints using Sphere's Risk Model classifications."
					: "Choose to apply constraints using user-defined custom categories."}
			</Text>
			<div className="max-h-[400px] h-[400px] min-h-0 overflow-hidden flex flex-col">
				{assetAllocation.type === "TAG" ? (
					<ClassificationOptionsTable
						mode={mode}
						selectedClassificationUuid={assetAllocation.instrumentClassification?.classificationUuid}
						selectableUserClassifications={selectableAssetClassOptions}
						onChangeClassification={(newClassification) => {
							if (newClassification) {
								setAssetAllocation(() => ({
									instrumentClassification: newClassification,
									type: "TAG",
								}));
							}
						}}
						onClassificationLoad={(availableOptions) => {
							if (availableOptions && availableOptions.length > 0) {
								const opts = availableOptions.map((x) =>
									userClassificationId(assetAllocation.instrumentClassification!.classificationUuid!, x),
								);
								const filteredOpts =
									disabledRowIds && disabledRowIds.length > 0 ? opts.filter((x) => !disabledRowIds.includes(x)) : opts;
								setSelectableTag(filteredOpts);
								return;
							}
							setSelectableTag(undefined);
						}}
						optionsProvider={optionsProvider}
						multiSelectCtx={tagMultiSelectCtx}
					/>
				) : assetAllocation.type === "RISK_MODEL" ? (
					<AssetAllocationTable
						rows={rows}
						multiSelectCtx={riskModelMultiSelectCtx}
						mode={mode}
						selectableAssetClasses={selectableAssetClasses}
					/>
				) : null}
			</div>
		</Dialog>
	);
}

type AssetAllocationTableProps = {
	selectableAssetClasses: AssetAllocationDialogProps["selectableAssetClasses"];
	mode?: "checkbox" | "radio";
	multiSelectCtx: MultiSelectCtx<string>;
	rows: Array<AssetClass & { id: string }>;
};

const defaultAssetAllocationFilters = {
	granularity: [] as string[],
	assetClass: [] as string[],
	geography: [] as string[],
	microAssetClass: [] as string[],
	microGeography: [] as string[],
};

type AssetAllocationFilterOptions = { [k in keyof typeof defaultAssetAllocationFilters]: Option<string>[] };

function includesGuardFn(filter: string[], field: string) {
	if (filter.length === 0) {
		return true;
	}
	return filter.filter((k) => field.toLowerCase().search(k.toLowerCase()) >= 0).length > 0;
}

function AssetAllocationTable({ selectableAssetClasses, mode, multiSelectCtx, rows }: AssetAllocationTableProps) {
	const [filters, setFilters] = useState(defaultAssetAllocationFilters);
	const [search, setSearch] = useState("");

	const selectableFilters = useMemo(() => {
		// Each evaluation owns its options: the lists describe the asset classes this dialog was opened
		// with, and nothing outside this memo may hold or see them.
		const options: AssetAllocationFilterOptions = {
			granularity: [],
			assetClass: [],
			geography: [],
			microAssetClass: [],
			microGeography: [],
		};

		for (const el of selectableAssetClasses) {
			if (el.macroAssetClass && !find(options.assetClass, (x) => x.value === el.macroAssetClass)) {
				options.assetClass.push({ label: el.macroAssetClass, value: el.macroAssetClass });
			}

			if (el.microAssetClass && !find(options.microAssetClass, (x) => x.value === el.microAssetClass)) {
				options.microAssetClass.push({ label: el.microAssetClass, value: el.microAssetClass });
			}

			if (el.granularity && !find(options.granularity, (x) => x.value === el.granularity)) {
				options.granularity.push({ label: el.granularity, value: el.granularity });
			}

			if (el.macroGeography && !find(options.geography, (x) => x.value === el.macroGeography)) {
				options.geography.push({ label: el.macroGeography, value: el.macroGeography });
			}

			if (el.microGeography && !find(options.microGeography, (x) => x.value === el.microGeography)) {
				options.microGeography.push({ label: el.microGeography, value: el.microGeography });
			}
		}

		for (const list of typedObjectValues(options)) {
			list.sort(builtInSortFnFor("label"));
		}

		return options;
	}, [selectableAssetClasses]);

	const filteredAssetClasses = useMemo(() => {
		const selectedFilters = typedObjectValues(filters).flatMap((filter) => filter);
		if (selectedFilters.length === 0) {
			return rows;
		}

		return rows.filter(
			(row) =>
				includesGuardFn(filters.assetClass, row.macroAssetClass ?? "") &&
				includesGuardFn(filters.microAssetClass, row.microAssetClass ?? "") &&
				includesGuardFn(filters.geography, row.macroGeography ?? "") &&
				includesGuardFn(filters.microGeography, row.microGeography ?? "") &&
				includesGuardFn(filters.granularity, row.granularity ?? ""),
		);
	}, [filters, rows]);

	const { filtered } = useSearchable({
		collection: filteredAssetClasses,
		query: search,
		extractFn: (row) => Object.values(row).join(" "),
	});

	const checkboxColumnData = useSelectableTableColumn({
		mode,
		rows: filteredAssetClasses,
		selectBy: (r) => r.id ?? "",
		filteredRows: filtered,
		multiSelectCtx,
	});

	const columns = useMemo<TableColumn<AssetClass & { id: string }>[]>(
		() => [
			checkboxColumnData.column,
			{
				header: "",
				content: (x) => x.name,
				sortFn: builtInSortFnFor("name"),
				name: "assetAllocationName",
			},
		],
		[checkboxColumnData.column],
	);

	return (
		<>
			<div className="flex gap-2 mb-4">
				<DebouncedSearchInput
					externalQuery={search}
					onChange={setSearch}
					data-qualifier="AssetAllocationTable/Search"
					placeholder="Search asset allocation.."
					className="flex-1"
				/>
				<Select
					enableSearch
					options={selectableFilters.granularity}
					value={filters.granularity}
					multi
					placeholder="Granularity"
					onChange={(x) => setFilters((prev) => ({ ...prev, granularity: x }))}
					disabled={selectableFilters.granularity.length === 0}
					className="flex-1"
					triggerDataAttrs={{ "data-qualifier": "AssetAllocationTable/Filters/Granularity" }}
				/>
				<Select
					enableSearch
					options={selectableFilters.assetClass}
					value={filters.assetClass}
					multi
					placeholder="Macro asset class"
					onChange={(x) => setFilters((prev) => ({ ...prev, assetClass: x }))}
					disabled={selectableFilters.assetClass.length === 0}
					className="flex-1"
					triggerDataAttrs={{ "data-qualifier": "AssetAllocationTable/Filters/AssetClass" }}
				/>
				<Select
					enableSearch
					options={selectableFilters.geography}
					value={filters.geography}
					multi
					placeholder="Geography"
					onChange={(x) => setFilters((prev) => ({ ...prev, geography: x }))}
					disabled={selectableFilters.geography.length === 0}
					className="flex-1"
					triggerDataAttrs={{ "data-qualifier": "AssetAllocationTable/Filters/Geography" }}
				/>
				<Select
					enableSearch
					options={selectableFilters.microAssetClass}
					value={filters.microAssetClass}
					multi
					placeholder="Micro asset class"
					onChange={(x) => setFilters((prev) => ({ ...prev, microAssetClass: x }))}
					disabled={selectableFilters.microAssetClass.length === 0}
					className="flex-1"
					triggerDataAttrs={{ "data-qualifier": "AssetAllocationTable/Filters/MicroAssetClass" }}
				/>
				<Select
					enableSearch
					options={selectableFilters.microGeography}
					value={filters.microGeography}
					multi
					placeholder="Micro geo"
					onChange={(x) => setFilters((prev) => ({ ...prev, microGeography: x }))}
					disabled={selectableFilters.microGeography.length === 0}
					className="flex-1"
					triggerDataAttrs={{ "data-qualifier": "AssetAllocationTable/Filters/MicroGeography" }}
				/>
			</div>
			<Controller value={defaultAssetAllocationReportOrderBy}>
				{({ value: orderBy, onChange: onOrderByChange }) => (
					<BaseTable
						columns={columns}
						rows={search ? filtered : sortRows({ rows: filtered, columns, orderByArr: orderBy })}
						orderBy={search ? undefined : orderBy}
						onOrderByChange={onOrderByChange}
						onRowClick={(row) => {
							if (!checkboxColumnData.multiSelectCtx.disabled(row.id)) {
								checkboxColumnData.toggle(row.id);
							}
						}}
					/>
				)}
			</Controller>
		</>
	);
}

const defaultAssetAllocationReportOrderBy: Array<OrderBy<"assetAllocationName">> = [
	{ columnName: "assetAllocationName", direction: "asc" },
];

type SpawnAssetAllocationDialogProps = {
	selectableUserClassifications: UserInstrumentClassificationDto[];
	selectableAssetClasses: AssetClass[];
	disabledRowIds?: string[];
	onSubmit(data: AssetAllocationPayload): void;
	optionsProvider(classificationUuid: string): MaybePromise<Array<string>>;
	mode?: "checkbox" | "radio";
} & (
	| {
			classificationType: Extract<AssetAllocationType, "TAG">;
			initialValue?: {
				selectedOptions: Array<string>;
				classificationUuid: string;
			};
	  }
	| {
			classificationType: Extract<AssetAllocationType, "RISK_MODEL">;
			initialValue?: {
				selectedOptions?: Array<AssetClass>;
			};
	  }
);

export function spawnAssetAllocationDialog(props: SpawnAssetAllocationDialogProps): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<AssetAllocationDialog
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onSubmit={(payload) => {
					props.onSubmit(payload);
					resolve();
				}}
			/>
		)),
	);
}
